import { useState } from 'react';
import { HelpCircle, X, Clock, Umbrella, Building2, Briefcase, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const SEEN_KEY = 'resawork_planning_how_it_works_closed';

/**
 * A soft, dismissible "how the planning works" panel shown on the company
 * planning page. Explains the 4 sources of availability and where to change
 * each one. Persists dismissal in localStorage; users can always re-open it
 * via the "Comment ça marche ?" button.
 */
export default function PlanningHowItWorks() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(SEEN_KEY) !== '1';
    } catch (_) {
      return true;
    }
  });

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch (_) { /* ignore */ }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="planning-how-it-works-toggle"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
        Comment ça marche ?
      </button>
    );
  }

  return (
    <div
      data-testid="planning-how-it-works-panel"
      className="relative rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm"
    >
      <button
        type="button"
        onClick={close}
        data-testid="planning-how-it-works-close"
        aria-label="Fermer"
        className="absolute top-3 right-3 text-emerald-700 hover:text-emerald-900 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
          <HelpCircle className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-emerald-900">
            Comprendre votre planning en 30 secondes
          </h3>
          <p className="text-sm text-emerald-800/80 mt-1">
            Le planning et vos widgets publics s'appuient sur <strong>4 sources</strong>. Modifiez la bonne
            pour que les changements se reflètent immédiatement côté client.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 mt-5">
        <SourceCard
          icon={<Clock className="w-5 h-5" />}
          color="from-emerald-100 to-emerald-50 border-emerald-200 text-emerald-700"
          title="1. Horaires globaux"
          subtitle="Paramètres → Horaires"
          description="Les jours et créneaux (matin, après-midi, journée) où votre entreprise travaille. S'appliquent à tous les collaborateurs par défaut."
        />
        <SourceCard
          icon={<Briefcase className="w-5 h-5" />}
          color="from-blue-100 to-blue-50 border-blue-200 text-blue-700"
          title="2. Horaires par collaborateur"
          subtitle="Fiche collaborateur → Horaires"
          description="Optionnels. Un collab peut avoir son propre agenda (ex : Marie ne travaille pas le mercredi). Sinon il hérite des horaires globaux."
          badge="Nouveau"
        />
        <SourceCard
          icon={<Umbrella className="w-5 h-5" />}
          color="from-orange-100 to-orange-50 border-orange-200 text-orange-700"
          title="3. Congés collaborateur"
          subtitle="Planning → Ajouter un congé"
          description="Périodes où un collab est indispo (validées). Le widget grise ses dates quand il est choisi comme intervenant préféré."
        />
        <SourceCard
          icon={<Building2 className="w-5 h-5" />}
          color="from-amber-100 to-amber-50 border-amber-200 text-amber-700"
          title="4. Fermetures d'entreprise"
          subtitle="Planning → Fermetures"
          description="Jours où toute l'entreprise est fermée (ex : vacances collectives, pont). Bloquent tous les widgets, plus les jours fériés français par défaut."
        />
      </div>

      <div className="mt-4 pt-4 border-t border-emerald-100 flex items-start gap-2 text-xs text-emerald-800">
        <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <p>
          <strong>Astuce :</strong> vos widgets publics n'ont aucun cache HTTP. Toute modification
          apparaît chez le client dès qu'il recharge la page.
        </p>
      </div>
    </div>
  );
}

function SourceCard({ icon, color, title, subtitle, description, badge }) {
  return (
    <div className={`rounded-xl border p-3 bg-gradient-to-br ${color}`}>
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold">{title}</h4>
            {badge && (
              <span className="inline-flex items-center rounded-full bg-white text-[10px] font-medium px-1.5 py-0.5 border border-current">
                {badge}
              </span>
            )}
          </div>
          <p className="text-[11px] uppercase tracking-wide opacity-70 mt-0.5">{subtitle}</p>
          <p className="text-xs mt-1.5 opacity-90 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}
